/**
 * TEMU 活动报名：从可报商品结构组装 enroll.submit.global 的 productList 项。
 *
 * 规则（平台文档）：
 * - 服饰 isApparel=1：活动价写在 skc（activityPrice / siteActivityPriceList）
 * - 非服饰：活动价写在 sku
 * - 半托管：优先传各站点 siteActivityPriceList；场次 sessionIds 必填
 * - 全托管官方大促(13)：也需传 sessionIds
 */

export function marketingNeedsSessionIds(track, activityType) {
  const type = Number(activityType);
  if (track === 'semi') return true;
  return type === 13;
}

function toPositiveInt(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
}

function firstText(...candidates) {
  for (const c of candidates) {
    if (c == null) continue;
    const s = String(c).trim();
    if (s) return s;
  }
  return '';
}

function pickSuggestPrice(node) {
  if (!node || typeof node !== 'object') return null;
  const candidates = [
    node.suggestActivityPrice,
    node.activityPrice,
    node.dailyPrice,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return Math.trunc(n);
  }
  return null;
}

function firstSitePrices(sitePriceList) {
  if (!Array.isArray(sitePriceList) || sitePriceList.length === 0) {
    return { supply: null, activity: null, currency: null };
  }
  for (const site of sitePriceList) {
    const supply = firstText(site?.dailyPrice, site?.supplyPrice, site?.supplierPrice);
    const activity = firstText(site?.suggestActivityPrice, site?.activityPrice);
    const currency = firstText(site?.currency);
    if (supply || activity) {
      return { supply: supply || null, activity: activity || null, currency: currency || null };
    }
  }
  return { supply: null, activity: null, currency: null };
}

function extractSkcPrices(skc, apparel) {
  let supply = null;
  let activity = null;
  let currency = null;
  if (apparel) {
    supply = firstText(skc?.dailyPrice, skc?.supplyPrice, skc?.supplierPrice) || null;
    activity = firstText(skc?.suggestActivityPrice, skc?.activityPrice) || null;
    currency = firstText(skc?.currency) || null;
    const fromSites = firstSitePrices(skc?.sitePriceList);
    if (!supply && fromSites.supply) supply = fromSites.supply;
    if (!activity && fromSites.activity) activity = fromSites.activity;
    if (!currency && fromSites.currency) currency = fromSites.currency;
  } else {
    const skuList = Array.isArray(skc?.skuList) ? skc.skuList : [];
    for (const sku of skuList) {
      if (!supply) supply = firstText(sku?.dailyPrice, sku?.supplyPrice, sku?.supplierPrice) || null;
      if (!activity) activity = firstText(sku?.suggestActivityPrice, sku?.activityPrice) || null;
      if (!currency) currency = firstText(sku?.currency) || null;
      if (!supply || !activity) {
        const fromSites = firstSitePrices(sku?.sitePriceList);
        if (!supply && fromSites.supply) supply = fromSites.supply;
        if (!activity && fromSites.activity) activity = fromSites.activity;
        if (!currency && fromSites.currency) currency = fromSites.currency;
      }
      if (supply && activity) break;
    }
    if (!supply) supply = firstText(skc?.dailyPrice, skc?.supplyPrice) || null;
    if (!activity) activity = firstText(skc?.suggestActivityPrice, skc?.activityPrice) || null;
    if (!currency) currency = firstText(skc?.currency) || null;
  }
  return { supply, activity, currency };
}

function mapSiteActivityPrices(sitePriceList, overridePrice = null, allowedSiteIds = null) {
  if (!Array.isArray(sitePriceList) || sitePriceList.length === 0) return null;
  const allow = allowedSiteIds instanceof Set && allowedSiteIds.size > 0
    ? allowedSiteIds
    : null;
  const list = [];
  for (const site of sitePriceList) {
    const siteId = toPositiveInt(site?.siteId);
    if (siteId == null) continue;
    if (allow && !allow.has(String(siteId))) continue;
    const activityPrice = overridePrice != null
      ? toPositiveInt(overridePrice)
      : pickSuggestPrice(site);
    if (activityPrice == null) continue;
    list.push({ siteId, activityPrice });
  }
  return list.length ? list : null;
}

/** 从可报商品 / 场次列表收集可选经营站点 */
export function collectMarketingEnrollSiteOptions(products = [], sessions = []) {
  const map = new Map();
  const put = (siteId, siteName) => {
    const id = toPositiveInt(siteId);
    if (id == null) return;
    const key = String(id);
    if (map.has(key)) {
      if (siteName && !map.get(key).siteName) {
        map.set(key, { siteId: id, siteName: String(siteName) });
      }
      return;
    }
    map.set(key, {
      siteId: id,
      siteName: firstText(siteName) || `站点 ${id}`,
    });
  };

  (Array.isArray(sessions) ? sessions : []).forEach((session) => {
    put(session?.siteId, session?.siteName);
  });

  (Array.isArray(products) ? products : []).forEach((product) => {
    const raw = product?.raw && typeof product.raw === 'object' ? product.raw : product;
    const siteLists = [
      Array.isArray(raw?.sites) ? raw.sites : null,
      Array.isArray(raw?.siteList) ? raw.siteList : null,
      Array.isArray(product?.sites) ? product.sites : null,
    ].filter(Boolean);
    siteLists.forEach((list) => {
      list.forEach((site) => {
        if (site == null || typeof site !== 'object') return;
        put(site.siteId ?? site.id, site.siteName ?? site.name);
      });
    });
    const skcList = Array.isArray(raw?.skcList) ? raw.skcList : [];
    skcList.forEach((skc) => {
      (Array.isArray(skc?.sitePriceList) ? skc.sitePriceList : []).forEach((site) => {
        put(site?.siteId, site?.siteName);
      });
      (Array.isArray(skc?.skuList) ? skc.skuList : []).forEach((sku) => {
        (Array.isArray(sku?.sitePriceList) ? sku.sitePriceList : []).forEach((site) => {
          put(site?.siteId, site?.siteName);
        });
      });
    });
  });

  return Array.from(map.values()).sort((a, b) => a.siteId - b.siteId);
}

/** 从可报商品 raw 解析类目文案 */
export function resolveMarketingProductCategory(product) {
  const raw = product?.raw && typeof product.raw === 'object' ? product.raw : product;
  if (!raw || typeof raw !== 'object') return '';
  const direct = firstText(
    raw.categoryName,
    raw.catName,
    raw.leafCatName,
    raw.categoryLabel,
    raw.cateName,
    raw.fullCateName,
    product?.categoryLabel,
    product?.categoryName,
  );
  if (direct) return direct;
  const leaf = raw.leafCat && typeof raw.leafCat === 'object' ? raw.leafCat : raw.cat;
  if (leaf && typeof leaf === 'object') {
    const fromLeaf = firstText(leaf.catName, leaf.categoryName, leaf.name);
    if (fromLeaf) return fromLeaf;
  }
  const cats = Array.isArray(raw.categories)
    ? raw.categories
    : (Array.isArray(raw.catList) ? raw.catList : []);
  if (cats.length) {
    return cats
      .map((cat) => firstText(cat?.catName, cat?.categoryName, cat?.name))
      .filter(Boolean)
      .join(' / ');
  }
  return '';
}

/** 从可报商品 raw 解析类目 ID（用于级联类目筛选） */
export function resolveMarketingProductCategoryId(product) {
  const raw = product?.raw && typeof product.raw === 'object' ? product.raw : product;
  if (!raw || typeof raw !== 'object') return '';
  const leaf = raw.leafCat && typeof raw.leafCat === 'object' ? raw.leafCat : raw.cat;
  const candidates = [
    leaf?.catId,
    leaf?.categoryId,
    leaf?.id,
    raw.catId,
    raw.categoryId,
    raw.leafCatId,
    product?.categoryId,
    product?.catId,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return String(Math.trunc(n));
  }
  return '';
}

/**
 * 将可报商品展开为 SKC 展示行（颜色 / SKC / 货号 / 供货价 / 参考活动价）
 */
export function parseMarketingProductSkcItems(product) {
  const raw = product?.raw && typeof product.raw === 'object' ? product.raw : product;
  const apparel = Number(raw?.isApparel ?? product?.isApparel) === 1;
  let skcList = Array.isArray(raw?.skcList) ? raw.skcList : [];
  if (!skcList.length && Array.isArray(raw?.productSkcList)) {
    skcList = raw.productSkcList;
  }
  if (!skcList.length) {
    return [{
      skcId: null,
      colorLabel: '—',
      skcName: '',
      supplierCode: '',
      imageUrl: firstText(raw?.productImageUrl, raw?.mainImageUrl, raw?.thumbUrl) || '',
      supplyPrice: null,
      activityPrice: null,
      currency: product?.currency || raw?.currency || null,
    }];
  }
  return skcList.map((skc) => {
    const skcId = toPositiveInt(skc?.skcId) ?? toPositiveInt(skc?.productSkcId);
    const color = firstText(skc?.color, skc?.colorName, skc?.specName, skc?.skcName);
    const size = firstText(skc?.size, skc?.sizeName);
    const colorLabel = size
      ? (color ? `${color}-${size}` : size)
      : (color || '—');
    const skcName = skcId != null
      ? String(skcId)
      : firstText(skc?.skcCode, skc?.skcExtCode);
    const prices = extractSkcPrices(skc, apparel);
    return {
      skcId,
      colorLabel,
      skcName,
      supplierCode: firstText(skc?.extCode, skc?.supplierCode, skc?.skcExtCode, skc?.outerGoodsUrl),
      imageUrl: firstText(skc?.thumbUrl, skc?.imageUrl, skc?.mainImageUrl, skc?.previewImgUrl),
      supplyPrice: prices.supply,
      activityPrice: prices.activity,
      currency: prices.currency || product?.currency || raw?.currency || null,
    };
  });
}

/**
 * @param {object} product 可报商品（含 raw / isApparel / suggestActivityStock）
 * @param {{
 *   activityStock?: number,
 *   sessionIds?: number[],
 *   siteIds?: number[],
 *   skcActivityPrices?: Record<string, number|string>,
 * }} options
 */
export function buildMarketingEnrollProductItem(product, options = {}) {
  const productId = toPositiveInt(product?.productId);
  if (productId == null) {
    throw new Error('货品 ID 无效');
  }
  const suggestStock = toPositiveInt(product?.suggestActivityStock)
    ?? toPositiveInt(product?.targetActivityStock);
  const activityStock = toPositiveInt(options.activityStock) ?? suggestStock;
  if (activityStock == null) {
    throw new Error(`货品 ${productId} 请填写活动申报库存`);
  }

  const raw = product?.raw && typeof product.raw === 'object' ? product.raw : product;
  const isApparel = Number(raw?.isApparel ?? product?.isApparel) === 1;
  const skcSource = Array.isArray(raw?.skcList) ? raw.skcList : [];
  if (skcSource.length === 0) {
    throw new Error(`货品 ${productId} 缺少 SKC 信息，请重新加载可报商品`);
  }

  const priceDrafts = options.skcActivityPrices && typeof options.skcActivityPrices === 'object'
    ? options.skcActivityPrices
    : {};
  const allowedSiteIds = Array.isArray(options.siteIds) && options.siteIds.length
    ? new Set(
      options.siteIds
        .map((id) => toPositiveInt(id))
        .filter((id) => id != null)
        .map(String),
    )
    : null;

  const skcList = skcSource.map((skc) => {
    const skcId = toPositiveInt(skc?.skcId);
    if (skcId == null) {
      throw new Error(`货品 ${productId} 存在无效 SKC`);
    }
    const draftPrice = toPositiveInt(priceDrafts[String(skcId)]);
    const item = { skcId };
    const skuSource = Array.isArray(skc?.skuList) ? skc.skuList : [];
    item.skuList = skuSource.map((sku) => {
      const skuId = toPositiveInt(sku?.skuId);
      if (skuId == null) {
        throw new Error(`货品 ${productId} / SKC ${skcId} 存在无效 SKU`);
      }
      const skuItem = { skuId };
      if (!isApparel) {
        const sitePrices = mapSiteActivityPrices(sku?.sitePriceList, draftPrice, allowedSiteIds);
        if (sitePrices) {
          skuItem.siteActivityPriceList = sitePrices;
        } else {
          const price = draftPrice ?? pickSuggestPrice(sku);
          if (price != null) skuItem.activityPrice = price;
        }
      }
      return skuItem;
    });

    if (isApparel) {
      const sitePrices = mapSiteActivityPrices(skc?.sitePriceList, draftPrice, allowedSiteIds);
      if (sitePrices) {
        item.siteActivityPriceList = sitePrices;
      } else {
        const price = draftPrice ?? pickSuggestPrice(skc);
        if (price != null) item.activityPrice = price;
      }
    }
    return item;
  });

  const out = {
    productId,
    activityStock,
    skcList,
  };
  const sessionIds = Array.isArray(options.sessionIds)
    ? options.sessionIds.map(toPositiveInt).filter((id) => id != null)
    : [];
  if (sessionIds.length) {
    out.sessionIds = sessionIds;
  }
  return out;
}

/**
 * @param {Array} products
 * @param {Record<string, {
 *   activityStock?: number,
 *   sessionIds?: number[],
 *   siteIds?: number[],
 *   skcActivityPrices?: Record<string, number|string>,
 * }>} drafts by productId
 */
export function buildMarketingEnrollProductList(products, drafts = {}) {
  if (!Array.isArray(products) || products.length === 0) {
    throw new Error('请先选择要报名的商品');
  }
  return products.map((product) => {
    const key = String(product?.productId);
    const draft = drafts[key] || {};
    return buildMarketingEnrollProductItem(product, draft);
  });
}

export function summarizeMarketingEnrollResult(res) {
  const successCount = Number(res?.successCount) || 0;
  const failCount = Number(res?.failCount) || 0;
  const failList = Array.isArray(res?.failList) ? res.failList : [];
  const platformOk = res?.success !== false;
  const parts = [];
  if (successCount > 0) parts.push(`成功 ${successCount}`);
  if (failCount > 0) parts.push(`失败 ${failCount}`);
  if (!parts.length) {
    if (!platformOk && res?.errorMsg) return String(res.errorMsg);
    return '平台未返回报名结果';
  }
  let msg = `报名完成：${parts.join('，')}`;
  if (failList.length) {
    const first = failList[0];
    const detail = first?.failMsg || (first?.productId != null ? `货品 ${first.productId}` : '');
    if (detail) msg += `；例如：${detail}`;
  }
  return msg;
}
